'use client';

import { ObligationRulesGeneratorConfirmationCalendar } from '@commenda/api';
import {
  Button,
  DataTable,
  LoadingIndicator,
  type ColumnGroup,
} from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { dedupeConfirmationRows } from './confirmationRows';
import { type PreviewRow } from './confirmationRows.types';
import { WizardModuleCard } from './WizardModuleCard';

// Generation status: denied / applies / needs confirmation. A confirmed item flips to "Applies".
const StatusBadge = ({ row }: { row: PreviewRow }) => {
  const { label, className } =
    row.decision === 'DENIED'
      ? {
          label: 'Denied',
          className:
            'bg-bg-secondary text-text-tertiary border border-stroke-3',
        }
      : row.mode === 'GENERATE_AS_CONFIRMATION' && !row.decision
        ? {
            label: 'Needs confirmation',
            className: 'bg-utility-warning-50 text-utility-warning-700',
          }
        : {
            label: 'Applies',
            className: 'bg-utility-success-50 text-utility-success-700',
          };
  return (
    <span
      className={cn('rounded px-1.5 py-0.5 text-xs font-semibold', className)}
    >
      {label}
    </span>
  );
};

const CalendarLoading = () => (
  <div className="flex min-h-[200px] flex-col items-center justify-center gap-3">
    <LoadingIndicator size={24} />
    <p className="txt-small-regular text-text-tertiary">
      Generating your compliance calendar…
    </p>
  </div>
);

/**
 * Part 3 — generated-calendar preview, rendered with the same DataTable the Tax & Reporting
 * filings table uses (no grouping). Columns: Name, Generation status. Rows are collapsed to one per
 * filing type (a filing can generate several instances in a year, e.g. quarterly estimated tax).
 * With the flag OFF the confirmation endpoint is unavailable, so we fall back to the plain calendar
 * (all "Applies").
 */
export const Part3CalendarPreview = ({
  flagOn,
  confirmationData,
  confirmationError,
  mutateConfirmation,
  plainData,
  plainError,
  mutatePlain,
}: {
  flagOn: boolean;
  confirmationData?: { calendar: ObligationRulesGeneratorConfirmationCalendar };
  confirmationError?: unknown;
  mutateConfirmation: () => void;
  plainData?: { compliances: { id: string; title: string }[] };
  plainError?: unknown;
  mutatePlain: () => void;
}) => {
  const confirmationItems = confirmationData?.calendar?.items ?? [];
  const plainItems = plainData?.compliances ?? [];

  const refresh = () => (flagOn ? mutateConfirmation() : mutatePlain());

  const rows: PreviewRow[] = flagOn
    ? dedupeConfirmationRows(confirmationItems)
    : plainItems.map((item) => ({
        id: item.id,
        filingCompositeId: null,
        title: item.title,
        mode: undefined,
        decision: null,
        canResolve: false,
        ruleDescription: null,
        category: null,
        frequency: null,
        dueDate: null,
        effectiveYear: null,
      }));

  const columnGroups: ColumnGroup<PreviewRow, unknown>[] = [
    {
      id: 'preview',
      columns: [
        {
          id: 'title',
          accessorKey: 'title',
          accessorFn: null,
          header: 'Name',
          cell: ({ row }) => (
            <span className="txt-small-medium text-text-primary">
              {row.original.title}
            </span>
          ),
        },
        {
          id: 'status',
          accessorKey: 'status',
          accessorFn: null,
          header: 'Generation status',
          width: 180,
          cell: ({ row }) => <StatusBadge row={row.original} />,
        },
      ],
    },
  ];

  // The renderer revalidates these exact caches after each (synchronous) regeneration, so
  // `data === undefined` means "still fetching" and `error` means "failed".
  const showLoading = flagOn
    ? confirmationData === undefined && confirmationError === undefined
    : plainData === undefined && plainError === undefined;

  const emptyState = (
    <div className="flex flex-col items-start gap-3 py-4">
      <p className="txt-small-regular text-text-tertiary">
        No compliance items were generated for this fiscal year.
      </p>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={() => void refresh()}
      >
        Refresh
      </Button>
    </div>
  );

  return (
    <WizardModuleCard
      title="Your compliance calendar"
      description="Review the filings generated for this fiscal year."
    >
      {showLoading ? (
        <CalendarLoading />
      ) : !flagOn && plainError ? (
        <p className="txt-small-regular text-text-error">
          Could not load the compliance calendar.
        </p>
      ) : rows.length === 0 ? (
        emptyState
      ) : (
        <DataTable data={rows} columnGroups={columnGroups} />
      )}
    </WizardModuleCard>
  );
};
